import MidiEffect from './MidiEffect.js';
import ParameterContext from 'effects/ParameterContext.js';
import ParameterEffect from './ParameterEffect.js';
import MidiTrigger from './MidiTrigger.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';

class EveryEffect extends MidiEffect {
    constructor(sequencerEngine) {
        super('Every', sequencerEngine);

        // parameters for the arpeggiator
        this.everyParameter = new AbstractParameter(
            'every', this, (o, x) => x,
           {
               documentation: "The size of the cycle in triggers.",
               min: 1,
               max: 32,
               unit: '',
               defaultValue: 2
            });
        this.registerParameter(this.everyParameter);

        // parameters for the arpeggiator
        this.offsetParameter = new AbstractParameter(
            'offset', this, (o, x) => x,
           {
               documentation: "The number of triggers within a cycle before applying the parameter change",
               min: 0,
               max: 8,
               unit: '',
               defaultValue: 0
            });
        this.registerParameter(this.offsetParameter);

        // parameters for the arpeggiator
        this.gateParameter = new AbstractParameter(
            'gate', this, (o, x) => x,
           {
               min: 0,
               max: 1,
               unit: '',
               defaultValue: 1
            });
        this.registerParameter(this.gateParameter);

        // parameters for the arpeggiator
        this.gateProbabilityParameter = new AbstractParameter(
            'gateProbability', this, (o, x) => x,
            {
                min: 0,
                max: 100,
                unit: '',
                defaultValue: 100
            });
        this.registerParameter(this.gateProbabilityParameter);

        this.parameterEffects = [];

        // counts what trigger we're out
        this.counter = 0;
    }

    getVisualizedParameters() {
        return [this.everyParameter];
    }

    getText() {
        return this.everyParameter.get();
    }

    get documentation() {
        return "Alters a parameter every x many triggers.";
    }

    deleteParameterizer(effect) {
        let index = this.parameterEffects.indexOf(effect);
        this.parameterEffects.splice(index, 1);
    }

    newParameter() {
        let effect = new ParameterEffect(this.track);
        this.parameterEffects.push(effect);
        effect.registerContext(new ParameterContext().forTrack(this.track));
        effect.registerMixSession(this.track.mixSession);
        effect.registerTrack(this.track);
        return effect;
    }

    getParameters() {
        let parameters = super.getParameters();
        for (let effect of this.parameterEffects) {
            parameters = [... parameters, ... effect.getParameters()];
        }
        return parameters;
    }

    set every(e) { this.everyParameter.set(e); }
    set offset(e) { this.offsetParameter.set(e); }

    get every() {
        return Math.round(this.everyParameter.get());
    }

    get offset() {
        return Math.round(this.offsetParameter.get());
    }

    get gate() {
        return Math.round(this.gateParameter.get());
    }

    get gateProbability() {
        return this.gateProbabilityParameter.get();
    }

    onNewStep() {
        this.triggers = [];

        this.parameterEffects.forEach(
            p => p.onNewStep());
    }

    clear() {
        this.counter = 0;
        this.triggers = [];


        this.parameterEffects.forEach(
            p => p.clear());
    }

    output() {
        if (this.triggers.length === 0) {
            return this.triggers;
        }

        if (this.counter++ % this.every !== this.offset) {
            // we don't change anything about the triggers
            // unless it's the everyTh trigger
            // for example every 2, is equivalent to,
           // do something on every other trigger
            return this.triggers;
        }

        let gate = this.gate && 100.0 * Math.random() <= this.gateProbability;

        if (!gate) {
            return [];
        }

        let triggers = this.triggers;
        // now go through all the parameter effects
        for (let i in this.parameterEffects) {
            let parameterEffect = this.parameterEffects[i];
            triggers.forEach(
                t => parameterEffect.input(t));

            triggers = parameterEffect.output();
        }

        return triggers;
    }

    getJson() {
        return {
            ... super.getJson(),
            parameterEffects: this.parameterEffects.map(e => e.getJson())
        };
    }

    fromJson(json, isCopy=false) {
        let bulk = super.fromJson(json, isCopy);

        for (let i in json.parameterEffects) {
            let parameterEffect = new ParameterEffect(this.track);
            bulk = {
                ... bulk,
                ... parameterEffect.fromJson(json.parameterEffects[i], isCopy)
            };
            this.parameterEffects.push(parameterEffect);
            parameterEffect.registerContext(new ParameterContext().forTrack(this.track));
            parameterEffect.registerMixSession(this.track.mixSession);
            parameterEffect.registerTrack(this.track);
        }
        return bulk;
    }

    getCopy(track, ignoreId=true) {
        let x = new EveryEffect(track);
        x.fromJson(this.getJson(), ignoreId);
        return x;
    }
}

export default EveryEffect;
